<template>
  <div>
    <div class="card rounded-top-0 rounded-bottom-4 border-0 shadow">
      <div
          class="card-body text-center p-0"
      >
        <div
            class="booking-step-container py-3 px-sm-4 py-sm-4"
        >
          <div v-if="loading">
            <strong>{{ $t('loading') }}</strong>
          </div>

          <transition
              appear
              name="booking-step"
              mode="out-in"
          >
            <router-view
                v-if="event"
                key="step"
                @ready="updateBooking"
                :event="event"
                :saving="saving"
                :booking="booking"
                class="position-relative"
            >
            </router-view>
            <error-list v-else key="error"/>
          </transition>
        </div>

        <div class="d-flex p-4 pt-2"
             v-if="inPosMode() && apiError">
          <button
              class="btn btn-success flex-fill"
              @click="reload"
          >
            {{ $t('refresh') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Booking from '../models/Booking';
import eventBus from '../eventBus';
import BookingService from '../services/BookingService';
import EventService from '../services/EventService';
import posMixin from '../mixins/posMixin';
import EventBus from '../eventBus';

export default {
  props: [
    'code',
    'requestedEvent'
  ],
  inject: ['$zone'],

  mixins: [posMixin],

  data() {
    return {
      event: null,
      loading: false,
      apiError: false,
      booking: new Booking(),
      saving: false,
      nonExistingZone: false
    }
  },

  watch: {
    // Call again the method to get the event if the route changes
    '$route': 'init'
  },

  mounted() {
    // Trigger a reload
    eventBus.$on('reloadEvent', this.resetEvent);
  },

  created() {
    this.init();
  },

  computed: {
    /**
     * The zone from the QR code the user has scanned.
     *
     * @return {String|null}
     */
    qrZone() {
      return this.$zone();
    }
  },

  methods: {
    /**
     * Initialize booking component.
     */
    init() {
      this.setPreferredZone();

      if (this.requestedEvent) {
        this.event = this.requestedEvent;
        EventBus.$emit('setEvent', this.event);
      } else if (this.event === null || this.event.code !== this.code) {
        this.getEvent();
      }
    },

    /**
     * Get the requested event.
     *
     * @return Promise
     */
    getEvent() {
      this.loading = true;

      EventService.get(this.code, this.booking.preferred_zone).then(response => {
        const event = response.data.data;

        if (this.booking.preferred_zone !== null) {
          // Validate zone. If isolate zones is enabled the given zone must exist on the event.
          if (event.isolate_zones && !event.zones.includes(this.booking.preferred_zone)) {
            this.nonExistingZone = true;
            this.booking.preferred_zone = null;
          }
        }

        this.event = event;

        if (response.data.zone) {
          this.$router.replace({
            name: this.$route.name,
            params: this.$route.params,
            query: {
              ...this.$route.query,
              zone: response.data.zone
            }
          });
        }

        EventBus.$emit('setEvent', this.event);
      }).catch(error => {
        this.apiError = true;

        if (error.response.status !== 404) {
          return;
        }

        if (error.response.data.message) {
          eventBus.$emit('error', error.response.data.message);
          return;
        }

        eventBus.$emit('error', this.$t('eventNotFound'));
      }).finally(() => {
        this.loading = false;
      });
    },

    /**
     * Reset event to trigger a reload.
     */
    resetEvent() {
      this.event = null;
    },

    /**
     * Set preferred zone in booking, based on query parameter or POS setting.
     */
    setPreferredZone() {
      // Via query / QR code
      if (this.qrZone !== null && !this.nonExistingZone) {
        this.booking.preferred_zone = this.qrZone;

        return;
      }

      // Via POS?
      if (!this.inPosMode()) {
        return;
      }

      const pos = this.getPos();

      if (pos !== null && pos.preferred_zone !== null) {
        this.booking.preferred_zone = pos.preferred_zone;
      }
    },

    /**
     * Update booking.
     *
     * @param {Object} booking
     * @param {Boolean} save
     */
    updateBooking(booking, save) {
      this.booking = booking;
      this.apiError = false;

      if (save && !this.saving) {
        this.saving = true;

        BookingService.add(this.booking)
            .then(response => {
              if (typeof response.data.checkout_url !== 'undefined') {
                // Checkout
                window.location.href = response.data.checkout_url;
                return;
              }

              // Confirmation
              this.$router.push('/confirmation/' + response.data.token);
            })
            .catch(() => {
              this.apiError = true;
              this.saving = false;
            });
      }

      EventBus.$emit('setBooking', this.booking);
    },

    /**
     * Clear event code and reload POS.
     */
    reload() {
      this.clearEventCode();

      window.location.href = '/';
    }
  }
}
</script>
